import React, { useEffect } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import RouterSuspense from '~/app/common/RouterSuspense';
import { Route, useHistory, useParams } from "react-router";
import Details from '.';
import PrivateRoute from '~/app/PrivateRoute';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import PeriodicCheck from '~/periodicCheck';
import Issues from '~/issues/List';
import Documentation from '~/documentation';
import IssueDetails from "~/issues/Details";
import { useStoreActions, useStoreState } from '~/store';
import { Machine, Project } from '~/types';
import { ROUTES } from '~/app/Router';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import { MachineTabs } from '~/app/data/statuses/machine';
import Services from '~/services/List';
import ServiceDetail from '~/services/Details';
import AccessDenied from '~/accessControl/AccessDenied';
import Trainings from '~/training';
import TrainingForm from '~/training/Add';
import ServiceChecklist from '~/services/Details/ServiceChecklist';
import Equipments from '~/periodicCheck/Equipments';
import Communication from '~/communication';
import CommunicationRoleUsers from '~/communication/RoleUsers';
import CommunicationChat from '~/communication/Chat';
import { canAccessMachineOffline } from '~/sqlite/lastUsedCache';

export const MACHINES_DETAILS_ROUTES = {
    details: '/projects/:projectId/machines/:id',
    periodicCheck: '/projects/:projectId/machines/:id/periodic-check',
    periodicCheckEquipments: '/projects/:projectId/machines/:id/periodic-check/equipments',
    documentation: '/projects/:projectId/machines/:id/documentation/:equipmentId?',
    communication: '/projects/:projectId/machines/:id/communication',
    communicationRoleUsers: '/projects/:projectId/machines/:id/communication/roles/:roleId/users',
    communicationChat: '/projects/:projectId/machines/:id/communication/chat',
    machineCommunicationChat: '/machines/:id/communication/chat',
    issues: '/machines/:id/issues',
    openCriticalIssues: '/machines/:id/open-critical-issues',
    issueDetails: '/machines/:id/issues/:issueId',
    services: '/machines/:id/services',
    serviceDetails: '/machines/:id/services/:serviceId',
    registerTraining: '/machines/:id/training/add',
    listTraining: '/machines/:id/training',
    serviceCheck: '/machines/:id/services/:serviceId/service-check',
    timeManagementStart:'/timeManagement/startTime'
};

export type RouterComponent = React.FC

const Router: RouterComponent = () => {
    const history = useHistory();
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const {id, projectId} = useParams<{ id: Machine['id'], projectId: Project['id'] }>();
    const machine = useStoreState(state => state.machine.data);
    const updateLastUsedAction = useStoreActions(actions => actions.machines.updateLastUsed);
    const fetchLastUsedAction = useStoreActions(actions => actions.machines.fetchLastUsed);
    const MachineFetchAction = useStoreActions(actions => actions.machine.fetchMachine);
    const MachineDashboardFetchAction = useStoreActions(actions => actions.machine.fetchMachineDashboard);
    const inFavorite = useStoreState(state => state.machines.inFavorite(id));
    const cacheOpenedMachine = useStoreActions(actions => actions.machines.cacheOpenedMachine);
    const activeTab = useStoreState(state => state.machine.activeTab);
    const allowedOffline = canAccessMachineOffline(id, inFavorite);

    usePageTitle((machine.name || '').toUpperCase());

    useEffect(() => {
        if ((netWorkStatus === 0 || allowedOffline) && id) {
            (activeTab === MachineTabs.DASHBOARD ? MachineDashboardFetchAction : MachineFetchAction)(id).then(() => {
                if (netWorkStatus === 0) {
                    updateLastUsedAction({ machineId: id })
                        .then(() => fetchLastUsedAction())
                        .then(() => cacheOpenedMachine(id));
                }
            });
        } else {
            history.push(route(ROUTES.projects));
        }
    }, [MachineFetchAction, id, projectId, netWorkStatus, updateLastUsedAction, fetchLastUsedAction, allowedOffline, history, MachineDashboardFetchAction, activeTab, cacheOpenedMachine]);

    if (machine.id && !machine.hasAccessToCompany) {
        return <AccessDenied type={2} />
    }

    return (
        <RouterSuspense>
            <Switch>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.periodicCheckEquipments} component={Equipments} requiredPermissions={PERMISSION_GROUPS.readWritePeriodicCheck} />
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.periodicCheck} component={PeriodicCheck} requiredPermissions={PERMISSION_GROUPS.readWritePeriodicCheck}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.documentation} component={Documentation} requiredPermissions={PERMISSION_GROUPS.readMachineDocuments}/>
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.machineCommunicationChat} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.machineCommunicationChat} component={CommunicationChat} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                )}
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.communicationChat} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.communicationChat} component={CommunicationChat} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                )}
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.communicationRoleUsers} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.communicationRoleUsers} component={CommunicationRoleUsers} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                )}
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.communication} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.communication} component={Communication} requiredPermissions={PERMISSION_GROUPS.machineRegistration}/>
                )}
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.registerTraining} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.registerTraining} component={TrainingForm} requiredPermissions={PERMISSION_GROUPS.registerTraining}/>
                )}
                {netWorkStatus !== 0 ? (
                    <Redirect from={MACHINES_DETAILS_ROUTES.listTraining} to={ROUTES.projects} />
                ) : (
                    <PrivateRoute path={MACHINES_DETAILS_ROUTES.listTraining} component={Trainings} requiredPermissions={PERMISSION_GROUPS.training} />
                )}
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.issueDetails} component={IssueDetails} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.serviceCheck} component={ServiceChecklist} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.serviceDetails} component={ServiceDetail} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.issues} component={Issues} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.services} component={Services} requiredPermissions={PERMISSION_GROUPS.readServiceDetails}/>
                <PrivateRoute path={MACHINES_DETAILS_ROUTES.openCriticalIssues} component={Issues} requiredPermissions={PERMISSION_GROUPS.readIssues}/>
                <Route path={MACHINES_DETAILS_ROUTES.details} component={Details} />
            </Switch>
        </RouterSuspense>
    )
};

export default Router;
